import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, RefreshCw, BookOpen, Eye, Lightbulb } from "lucide-react";
import { api } from "@/lib/api";

// Sprint 8.E — Re-Entry-Modal für cancelled User auf /cockpit.
// Triggert auf Mount: GET /api/account/retention → wenn cancelled=true → Modal zeigen.
// LocalStorage `f4s_reentry_dismissed_<userId>` für Session-Dismiss.
// CTA "Pro Solo reaktivieren" → POST /api/account/reactivate → Reload Cockpit.
// CTA "Später entscheiden" → Modal schließen (bleibt bis nächster Login).

function dismissKey(userId) {
  return `f4s_reentry_dismissed_${userId}`;
}

function wasDismissed(userId) {
  try {
    return sessionStorage.getItem(dismissKey(userId)) === "1";
  } catch (_) {
    return false;
  }
}

function markDismissed(userId) {
  try {
    sessionStorage.setItem(dismissKey(userId), "1");
  } catch (_) {
    /* noop */
  }
}

export default function ReEntryModal({ user, onReactivated }) {
  const userId = user?.id || user?._id || "";
  const [state, setState] = useState({ ready: false, show: false, payload: null });
  const [reactivating, setReactivating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    if (wasDismissed(userId)) {
      setState({ ready: true, show: false, payload: null });
      return undefined;
    }
    api
      .get("/account/retention")
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        const shouldShow = d.state?.cancelled === true && d.state?.read_only === true;
        setState({
          ready: true,
          show: shouldShow,
          payload: d,
        });
      })
      .catch(() => {
        if (!cancelled) setState({ ready: true, show: false, payload: null });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!state.ready || !state.show || !state.payload) return null;

  const { assets, days_away, state: retState, first_name } = state.payload;
  const daysLeft = retState?.days_remaining ?? 0;
  const name = (first_name || "").trim();

  const handleLater = () => {
    markDismissed(userId);
    setState((s) => ({ ...s, show: false }));
  };

  const handleReactivate = async () => {
    setReactivating(true);
    setError(null);
    try {
      const res = await api.post("/account/reactivate");
      markDismissed(userId);
      setState((s) => ({ ...s, show: false }));
      if (onReactivated) onReactivated(res.data);
      // Reload um aktualisierten User-State zu holen
      setTimeout(() => {
        window.location.reload();
      }, 400);
    } catch (err) {
      // Sprint-1 Guard (26.08.2026): 409 needs_new_purchase -> CTA-Link zu Ablefy
      const detail = err?.response?.data?.detail;
      if (detail?.code === "needs_new_purchase" && detail?.ablefy_login_url) {
        setError(
          `Dein Zugang ist abgelaufen. Bitte starte einen neuen Kauf bei Ablefy: ${detail.ablefy_login_url}`,
        );
      } else {
        setError(
          detail?.code === "not_cancelled"
            ? "Account ist bereits aktiv."
            : "Reaktivierung fehlgeschlagen. Bitte versuche es später erneut.",
        );
      }
    } finally {
      setReactivating(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4"
        data-testid="reentry-modal"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-7 relative shadow-2xl"
        >
          <button
            type="button"
            onClick={handleLater}
            aria-label="Schließen"
            data-testid="reentry-modal-close"
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>

          <h2 className="text-xl font-extrabold tracking-tight text-[var(--fts-text-primary)]">
            Willkommen zurück{name ? `, ${name}` : ""}.
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Schön, dass du wieder da bist.
            {days_away > 0 ? ` Du warst ${days_away} Tag${days_away === 1 ? "" : "e"} weg.` : ""}
          </p>

          <div
            data-testid="reentry-assets"
            className="mt-5 rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-2"
          >
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
              Deine Flexpfad-Bibliothek ist noch da:
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <BookOpen className="w-4 h-4 text-[#4563BB] flex-shrink-0" />
              <span data-testid="reentry-bruecken-count">
                <strong>{assets?.bruecken ?? 0}</strong> Flexpfade gespeichert
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Eye className="w-4 h-4 text-[#4563BB] flex-shrink-0" />
              <span data-testid="reentry-muster-count">
                <strong>{assets?.muster ?? 0}</strong> Muster erkannt
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Lightbulb className="w-4 h-4 text-[#4563BB] flex-shrink-0" />
              <span>
                <strong>{assets?.hot_tips ?? 8}</strong> Hot Tips warten
              </span>
            </div>
          </div>

          <p
            data-testid="reentry-days-remaining"
            className="mt-4 text-xs text-slate-500"
          >
            ⏳ Daten verfügbar noch{" "}
            <strong>
              {daysLeft} Tag{daysLeft === 1 ? "" : "e"}
            </strong>{" "}
            — kein Verlust beim Reaktivieren.
          </p>

          {error && (
            <p data-testid="reentry-error" className="mt-3 text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={handleLater}
              data-testid="reentry-later"
              className="rounded-md border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Später entscheiden
            </button>
            <button
              type="button"
              onClick={handleReactivate}
              disabled={reactivating}
              data-testid="reentry-reactivate"
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#4563BB] hover:bg-[#3a55a5] disabled:opacity-50 text-white px-4 py-2.5 text-sm font-bold"
            >
              {reactivating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Pro Solo reaktivieren
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
